'use client';

import { useEffect, useState } from 'react';
import { UserDetails } from '@/utils/helpers';
import { CommentForm } from './CommentForm';
import { CommentContent } from './CommentContent';
import { CommentActions } from './CommentActions';
import { CommentWithUser } from './types';
import { CommentHeader } from './CommentHeader';
import ProfileAvatar from '../ProfileUser/ProfileAvatar';
import { OAuthLink } from '@/contexts/OAuthFlowContext';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { usePrevious } from '@/utils/hooks/usePrevious';
import { getCommentReplies, getToneCommentById } from '@/utils/supabase/queries';
import Button from '../Button';
import { ReplyCommentItem } from './ReplyCommentItem';

interface TopLevelCommentItemProps {
  supabase: SupabaseClient;
  comment: CommentWithUser;
  userDetails: UserDetails | null;
  toneId: number;
  toneUsername: string;
  limit: number;
  isApiSelect?: boolean;
  onReload?: (id: number) => void;
}

export function TopLevelCommentItem({
  comment,
  userDetails,
  toneId,
  toneUsername,
  limit,
  onReload,
  supabase,
  isApiSelect = false,
}: TopLevelCommentItemProps) {
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [nestedReply, setNestedReply] = useState<null | { id: number; username: string }>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [repliesLoaded, setRepliesLoaded] = useState(false);
  const [replies, setReplies] = useState<CommentWithUser[]>([]);
  const [hasMoreReplies, setHasMoreReplies] = useState(false);
  const previousRepliesCount = usePrevious(comment.replies_count);

  const hasReplies = (comment.replies_count || 0) > 0;
  const isOwner = userDetails?.id === comment.user_id;
  const canReply = !comment.is_deleted;

  useEffect(() => {
    if (nestedReply) {
      // scroll to the nested reply with top offset
      const element = document.getElementById(`nested-reply-${nestedReply.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [nestedReply]);

  useEffect(() => {
    // handle auto-expanding replies
    if (previousRepliesCount === undefined) return;
    if (previousRepliesCount !== comment.replies_count) {
      setShowReplies(true);
    }
  }, [previousRepliesCount, comment.replies_count]);

  const loadReplies = async () => {
    if (!comment.id || repliesLoaded || loadingReplies) return;
    setLoadingReplies(true);
    try {
      const replies = await getCommentReplies(supabase, toneId, comment.id, limit, 0);
      setReplies(replies);
      setHasMoreReplies((comment.replies_count || 0) > replies.length);
      setRepliesLoaded(true);
    } catch (error) {
      console.error('Error loading replies:', error);
    } finally {
      setLoadingReplies(false);
    }
  };

  const loadMoreReplies = async () => {
    if (!comment.id || loadingReplies) return;
    setLoadingReplies(true);
    try {
      const _replies = await getCommentReplies(supabase, toneId, comment.id, limit, replies.length);
      const newReplies = [...replies, ..._replies];
      setReplies(newReplies);
      setHasMoreReplies((comment.replies_count || 0) > newReplies.length);
      setRepliesLoaded(true);
    } catch (error) {
      console.error('Error loading more replies:', error);
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleOnReload = async () => {
    onReload?.(comment.id!);
  };

  const onReplyReload = async (id: number) => {
    const comment = await getToneCommentById(supabase, id);
    // update the replies
    if (comment) {
      const newReplies = replies.map((c) => (c.id === id ? comment : c));
      setReplies(newReplies);
    } else {
      // remove the comment from the replies
      setReplies((prev) => prev.filter((c) => c.id !== id));
      handleOnReload();
    }
  };

  const handleReplySubmit = async (id: number) => {
    setShowReplyForm(false);
    setNestedReply(null);
    const newComment = await getToneCommentById(supabase, id);
    if (newComment) {
      setReplies((prev) => [...prev, newComment]);
      handleOnReload();
    }
  };

  const handleEditSubmit = async (id: number) => {
    setIsEditing(false);
    handleOnReload();
  };

  const handleToggleReplies = async () => {
    if (!showReplies && !repliesLoaded) await loadReplies();
    setShowReplies(!showReplies);
  };

  const handleNestedReply = ({ id, username }: { id: number; username: string }) => {
    setNestedReply({ id, username });
  };

  return (
    <div className="flex items-start gap-3">
      <OAuthLink
        href={isApiSelect ? `/api/v1/select/${comment.username}` : `/${comment.username}`}
        className="flex items-center gap-2"
      >
        <ProfileAvatar
          username={comment.username || ''}
          avatarUrl={comment.avatar_url || ''}
          ownProfile={false}
          size={32}
        />
      </OAuthLink>
      <div className="flex flex-col gap-3 w-full">
        <div className="flex flex-col gap-3">
          {/* Comment Header */}
          <CommentHeader comment={comment} toneUsername={toneUsername} isApiSelect={isApiSelect} />

          {/* Comment Content */}
          {isEditing ? (
            <CommentForm
              toneId={toneId}
              userDetails={userDetails!}
              parentId={comment.parent_id}
              editingComment={comment}
              onSubmit={handleEditSubmit}
              onCancel={() => setIsEditing(false)}
            />
          ) : !comment.is_deleted ? (
            <CommentContent content={comment.content!} isApiSelect={isApiSelect} />
          ) : null}

          {/* Comment Actions */}
          {!comment.is_deleted && !isEditing && (
            <CommentActions
              comment={comment}
              userDetails={userDetails}
              isOwner={isOwner}
              canReply={canReply}
              onReply={() => setShowReplyForm(!showReplyForm)}
              onEdit={() => setIsEditing(true)}
              onDelete={() => handleOnReload()}
            />
          )}

          {/* Reply Form */}
          {showReplyForm && userDetails && (
            <div className="mt-4">
              <CommentForm
                key={`reply-${comment.id}`}
                toneId={toneId}
                userDetails={userDetails}
                parentId={comment.id || 0}
                onSubmit={handleReplySubmit}
                onCancel={() => setShowReplyForm(false)}
                placeholder={`Replying to @${comment.username}...`}
                initialContent={``}
              />
            </div>
          )}
        </div>

        {/* Replies */}
        {hasReplies && (
          <div className="flex flex-col gap-3">
            <button
              onClick={handleToggleReplies}
              className="flex items-center gap-2 w-fit disabled:opacity-50"
              style={{ color: '#40A6FF' }}
            >
              {showReplies ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              {comment.replies_count || 0}{' '}
              {(comment.replies_count || 0) === 1 ? 'reply' : 'replies'}
              {loadingReplies && (
                <div className="animate-spin w-4 h-4 border border-zinc-400 border-t-transparent rounded-full" />
              )}
            </button>

            {showReplies && (
              <div className="flex flex-col gap-6" id={`replies-${comment.id}`}>
                {replies.map((reply) => (
                  <ReplyCommentItem
                    key={reply.id}
                    supabase={supabase}
                    comment={reply}
                    userDetails={userDetails}
                    toneId={toneId}
                    toneUsername={toneUsername}
                    onReload={() => onReplyReload(reply.id!)}
                    onNestedReply={handleNestedReply}
                    isApiSelect={isApiSelect}
                  />
                ))}
                {nestedReply && (
                  <div id={`nested-reply-${nestedReply.id}`}>
                    <CommentForm
                      key={`nested-reply-${nestedReply.id}`}
                      toneId={toneId}
                      userDetails={userDetails}
                      parentId={nestedReply.id}
                      onSubmit={handleReplySubmit}
                      onCancel={() => setNestedReply(null)}
                      placeholder={`Replying to @${nestedReply.username}...`}
                      initialContent={`@${nestedReply.username} `}
                    />
                  </div>
                )}
                {hasMoreReplies && (
                  <div className="flex justify-center">
                    <Button
                      onClick={loadMoreReplies}
                      disabled={loadingReplies}
                      variant="slim"
                      isSecondary
                    >
                      {loadingReplies ? 'Loading...' : 'View More'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
